import { useEffect, useState, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { impersonateUser } from "../../store/superAdmin/impersonationSlice";
import {
  fetchFilteredUsersByTokenAndRole,
  resetFilteredUsersState,
} from "../../store/common/searchSlice";
import ConfirmModal from "../../components/utilities/confirmModal";
import { Helmet } from "react-helmet-async";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminMetaRow,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";

const roles = ["User", "Operations", "Admin", "Admin Read", "Side Admin", "Production"];
const PAGE_LIMIT = 50;

const Impersonation = () => {
  const [role, setRole] = useState("User");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [modalUser, setModalUser] = useState<any>(null);
  const isFirstRender = useRef(true); // skip initial duplicate call

  const dispatch = useAppDispatch();
  const {
    filteredUsers = [],
    // error,
    loading,
  } = useAppSelector((s) => s.search);

  // Fetch users when role changes
  useEffect(() => {
    dispatch(resetFilteredUsersState());
    dispatch(fetchFilteredUsersByTokenAndRole({ token: "", role }));
    setPage(1);
    return () => {
      void dispatch(resetFilteredUsersState());
    };
  }, [role, dispatch]);

  // Debounced search (skip on first render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // skip the very first run
    }

    const timer = setTimeout(() => {
      dispatch(fetchFilteredUsersByTokenAndRole({ token: search, role }));
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [search, role, dispatch]);

  // Paginate users client-side
  const paginatedUsers = useMemo(() => {
    const list = Array.isArray(filteredUsers) ? filteredUsers : [];
    const start = (page - 1) * PAGE_LIMIT;
    return list.slice(start, start + PAGE_LIMIT);
  }, [filteredUsers, page]);

  const totalPages = Math.ceil(
    (Array.isArray(filteredUsers) ? filteredUsers.length : 0) / PAGE_LIMIT
  );

  // Handle impersonation
  const handleConfirm = async () => {
    if (!modalUser) return;
    const res = await dispatch(impersonateUser(modalUser.user_id));
    if (impersonateUser.fulfilled.match(res)) window.location.reload();
  };

  const colCount = 6;

  return (
    <>
      <Helmet>
        <title>Login as</title>
      </Helmet>

      <AdminPageShell>
        <AdminPageHeader
          title="Login As"
          subtitle="Impersonate a user to test their portal experience"
        >
          <AdminSearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by Token…"
          />
        </AdminPageHeader>

        {/* Role filter */}
        <div className="flex gap-2 flex-wrap px-0.5">
          {roles.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => {
                setRole(r);
                setSearch("");
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
                r === role
                  ? "bg-[#2fa9bf] text-white border-[#2fa9bf]"
                  : "bg-white text-[#4A4A48] border-[#DDDDD8] hover:bg-[#F8F8F6]"
              }`}
            >
              {r}
            </button>
          ))}
        </div>

        <AdminMetaRow
          left={<span>{role} records</span>}
          right={
            <AdminPagination
              page={page}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages || 1, p + 1))}
              disabled={loading.filteredUsers}
            />
          }
        />

        {/* Users Table */}
        <AdminTableCard>
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                {["S.No", "User ID", "Token", "Name", "Email", "Login"].map(
                  (h) => (
                    <th
                      key={h}
                      className={
                        h === "Login" ? `${adminTableHead} text-center` : adminTableHead
                      }
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {loading.filteredUsers ? (
                <AdminTableState
                  colSpan={colCount}
                  variant="loading"
                  message="Loading users…"
                />
              ) : paginatedUsers.length ? (
                paginatedUsers.map((u, i) => (
                  <tr
                    key={u.user_id}
                    className="border-b border-[#F0F0EC] last:border-0 hover:bg-[#FAFAF8] transition-colors"
                  >
                    <td className={adminTableCell}>
                      {(page - 1) * PAGE_LIMIT + i + 1}
                    </td>
                    <td className={adminTableCell}>{u.user_id}</td>
                    <td className={adminTableCell}>{u.token}</td>
                    <td className={adminTableCell}>{u.user_name}</td>
                    <td className={adminTableCell}>{u.email}</td>
                    <td className={adminTableCell}>
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={() => setModalUser(u)}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-[#2fa9bf] text-white hover:bg-[#2296aa] transition"
                        >
                          Login
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <AdminTableState
                  colSpan={colCount}
                  variant="empty"
                  message="No users found"
                />
              )}
            </tbody>
          </table>
        </AdminTableCard>

        <ConfirmModal
          isOpen={!!modalUser}
          onClose={() => setModalUser(null)}
          onConfirm={handleConfirm}
          title="Confirm Impersonation"
          message={
            modalUser ? (
              <>
                Are you sure you want to impersonate{" "}
                <strong>{modalUser.email}</strong>?
              </>
            ) : null
          }
          confirmColor="blue"
          confirmLabel="Yes, Confirm"
          cancelLabel="Cancel"
        />
      </AdminPageShell>
    </>
  );
};
export default Impersonation;
